import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/AxiosConnection";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { BarChart3, TrainIcon, ArrowUpDown } from "lucide-react";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import { Vortex } from "react-loader-spinner";

// Import components
import ValidationAlert from "./ValidationAlert";
import NoDataAlert from "./NoDataAlert";
import SummaryReport from "./SummaryReport";
import TrainDetailsTable from "./TrainDetailsTable";
import ReportsFilters from "./ReportsFilters";

// Import images
import colorLogo from "../../assets/login/ColorLogo.png";
import logo1 from "../../assets/header/HABD.png";
import logo2 from "../../assets/login/DMW_Patiala.png";

const Reports = () => {
  const [coachType, setCoachTypes] = useState({
    all: true,
    wagon: true,
    lhb: true,
    icf: true,
  });

  const [dates, setDates] = useState({
    fromDate: "",
    toDate: "",
  });

  const [direction, setDirection] = useState({
    all: true,
    up: true,
    down: true,
  });

  const [alarmType, setAlarmType] = useState({
    all: true,
    alarms: true,
    warning: true,
  });

  const [location, setLocation] = useState({
    all: true,
  });

  const [trainNumber, setTrainNumber] = useState("");
  const [coachNumber, setCoachNumber] = useState("");
  const [rfid, setRfid] = useState("");

  const [sites, setSites] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    fromDate: "",
    toDate: "",
    coachType: null,
    directions: null,
    hasAlarm: null,
    hasWarning: null,
    locations: [],
    trainNumber: null,
    coachNumber: null,
    hexRFID: null,
  });

  const [validationError, setValidationError] = useState(null);
  const [locationPairs, setLocationPairs] = useState([]);
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNoDataPopup, setShowNoDataPopup] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [sortOption, setSortOption] = useState({ field: null, order: null });
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortIconRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const locationHook = useLocation();

  const formatDateForFile = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target) &&
        sortIconRef.current &&
        !sortIconRef.current.contains(event.target)
      ) {
        setIsSortOpen(false);
      }
    };

    if (isSortOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortOpen]);

  // Prevent scrolling when loading
  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup on component unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [loading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setResponseData(null);
      setShowNoDataPopup(false);

      if (!selectedFilters.locations || selectedFilters.locations.length === 0) {
        setLoading(false);
        setShowNoDataPopup(true);
        return;
      }

      const requestParams = {
        siteDbs: selectedFilters.locations.join(","),
        fromDate: selectedFilters.fromDate,
        toDate: selectedFilters.toDate,
        coachType: selectedFilters.coachType,
        direction: selectedFilters.directions,
        hasAlarm: selectedFilters.hasAlarm,
        hasWarning: selectedFilters.hasWarning,
        trainNumber: selectedFilters.trainNumber,
        coachNumber: selectedFilters.coachNumber,
        hexRFID: selectedFilters.hexRFID,
      };

      const response = await api.get("/report/getTrainData", {
        params: requestParams,
      });

      if (
        !response.data ||
        !response.data.trainDataBySiteDb ||
        Object.keys(response.data.trainDataBySiteDb).length === 0 ||
        response.data.totalResults === 0
      ) {
        setResponseData(null);
        setShowNoDataPopup(true);
      } else {
        setResponseData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setResponseData(null);
      setShowNoDataPopup(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get("/users/get-sites", {
          params: { userId: user.id },
        });
        const locationData = response.data.sites;
        setSites(locationData);

        const initialLocationState = locationData.reduce(
          (acc, site) => {
            acc[site.siteName] = true;
            return acc;
          },
          { all: true }
        );

        const locationPairs = locationData.map(
          (site) => `${site.siteDbName} = ${site.siteName}`
        );
        setLocation(initialLocationState);
        setLocationPairs(locationPairs);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setValidationError("Failed to fetch locations. Please try again.");
      }
    };

    fetchLocations();
  }, [user.id]);

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setDates({
      fromDate: formatDate(sevenDaysAgo),
      toDate: formatDate(today),
    });
  }, [locationHook.pathname]);

  useEffect(() => {
    if (!dates.fromDate || !dates.toDate || sites.length === 0) {
      return;
    }

    const coachTypeValue = coachType.all
      ? null
      : Object.keys(coachType)
        .filter((key) => key !== "all" && coachType[key])
        .join(",");

    const directionValue = direction.all
      ? null
      : Object.keys(direction)
        .filter((key) => key !== "all" && direction[key])
        .join(",");

    let hasAlarm = null;
    let hasWarning = null;

    if (!alarmType.all) {
      if (alarmType.alarms && alarmType.warning) {
        hasAlarm = 0;
        hasWarning = 0;
      } else if (alarmType.alarms) {
        hasAlarm = 1;
        hasWarning = 0;
      } else if (alarmType.warning) {
        hasAlarm = 0;
        hasWarning = 1;
      }
    }

    const selectedLocations = Object.keys(location)
      .filter((key) => key !== "all" && location[key])
      .map((siteName) => {
        const site = sites.find((s) => s.siteName === siteName);
        return site ? site.siteDbName : null;
      })
      .filter(Boolean);

    setSelectedFilters({
      fromDate: dates.fromDate,
      toDate: dates.toDate,
      coachType: coachTypeValue,
      directions: directionValue,
      hasAlarm,
      hasWarning,
      locations: selectedLocations,
      trainNumber: trainNumber || null,
      coachNumber: coachNumber || null,
      hexRFID: rfid || null,
    });
  }, [coachType, direction, alarmType, location, dates, sites, trainNumber, coachNumber, rfid]);

  useEffect(() => {
    if (
      initialLoad &&
      selectedFilters.fromDate &&
      selectedFilters.toDate &&
      selectedFilters.locations.length > 0
    ) {
      fetchData();
      setInitialLoad(false);
    }
  }, [selectedFilters, initialLoad]);

  // Memoized Sorting & Grouping Logic
  const groupedTrains = useMemo(() => {
    if (!responseData || !responseData.trainDataBySiteDb) return [];

    // Flatten train data with SiteName
    let allTrains = Object.entries(responseData.trainDataBySiteDb).flatMap(
      ([siteName, trainList]) =>
        trainList.map((train) => ({
          ...train,
          SiteName: siteName,
        }))
    );

    // Apply sorting based on sortOption
    if (sortOption.field) {
      const order = sortOption.order === "asc" ? 1 : -1;
      if (sortOption.field === "trainType") {
        allTrains.sort((a, b) => (a.Description || "").localeCompare(b.Description || "") * order);
      } else if (sortOption.field === "trainDate") {
        allTrains.sort((a, b) => (a.TrainId || "").localeCompare(b.TrainId || "") * order);
      } else if (sortOption.field === "siteName") {
        allTrains.sort((a, b) => (a.SiteName || "").localeCompare(b.SiteName || "") * order);
      }
    }

    // Rebuild groupedTrains structure
    let cumulativeCount = 0;
    const groupedList = [];
    const siteGroups = {};

    allTrains.forEach((train) => {
      if (!siteGroups[train.SiteName]) {
        siteGroups[train.SiteName] = [];
      }
      siteGroups[train.SiteName].push(train);
    });

    Object.entries(siteGroups).forEach(([siteName, trainList]) => {
      const siteTrains = trainList.map((train, idx) => ({
        ...train,
        serialNo: cumulativeCount + idx + 1,
      }));
      cumulativeCount += trainList.length;
      groupedList.push({ siteName, trains: siteTrains });
    });

    return groupedList;
  }, [responseData, sortOption]);

  const totalTrains = useMemo(
    () => groupedTrains.reduce((sum, grp) => sum + grp.trains.length, 0),
    [groupedTrains]
  );

  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!dates.fromDate || !dates.toDate) {
      setValidationError("Please select both From Date and To Date");
      return false;
    }

    const fromDate = new Date(dates.fromDate);
    const toDate = new Date(dates.toDate);
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    const threeMonthsInMs = 90 * 24 * 60 * 60 * 1000; 
    if (toDate - fromDate > threeMonthsInMs) {
      setValidationError("Date range cannot exceed 3 months");
      return false;
    }

    if (fromDate > today) {
      setValidationError("From Date cannot be in the future");
      return false;
    }

    if (toDate > today) {
      setValidationError("To Date cannot be in the future");
      return false;
    }

    if (fromDate > toDate) {
      setValidationError("From Date should be before To Date");
      return false;
    }

    const validateDropdown = (category, categoryName) => {
      const isAnySelected = Object.keys(category)
        .filter((key) => key !== "all")
        .some((key) => category[key]);

      if (!isAnySelected) {
        setValidationError(`Please select at least one ${categoryName}`);
        return false;
      }
      return true;
    };

    if (!validateDropdown(coachType, "Coach Type")) return false;
    if (!validateDropdown(direction, "Direction")) return false;
    if (!validateDropdown(alarmType, "Alarm Type")) return false;
    if (!validateDropdown(location, "Location")) return false;

    setValidationError(null);
    return true;
  };

  const handleSearch = () => {
    if (validateForm()) {
      fetchData();
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDates({
      ...dates,
      [name]: value,
    });
  };

  const handleTrainNumberChange = (e) => {
    setTrainNumber(e.target.value);
  };

  const handleCoachNumberChange = (e) => {
    setCoachNumber(e.target.value);
  };

  const handleRfidChange = (e) => {
    setRfid(e.target.value);
  };

  const handleAllChange = (setState, state, categoryName) => {
    setState((prevState) => {
      const newState = !prevState.all;
      const updatedState = Object.keys(prevState).reduce((acc, key) => {
        acc[key] = newState;
        return acc;
      }, {});

      return updatedState;
    });
  };

  const handleIndividualChange = (key, setState, state, categoryName) => {
    setState((prevState) => {
      const updatedState = {
        ...prevState,
        [key]: !prevState[key],
      };

      const allChecked = Object.keys(updatedState)
        .filter((k) => k !== "all")
        .every((k) => updatedState[k]);

      updatedState.all = allChecked;

      return updatedState;
    });
  };

  const handleSortSelect = (field, order) => {
    setSortOption({ field, order });
    setIsSortOpen(false);
  };

  const exportToExcel = async () => {
    setDownloadLoading((prev) => ({ ...prev, excel: true }));
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Train Data");

    // Define headers (excluding Download column)
    const headers = [
      "Sr. No.",
      "Site Name",
      "Train Date",
      "Train Time",
      "Train Name",
      "Train Number",
      "Train Type",
      "Direction",
      "No Of Axle",
      "Avg Speed",
      "Alarm",
      "Warning",
    ];

    try {
      // Fetch images
      const colorLogoBuffer = await fetch(colorLogo).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch ColorLogo.png");
        return res.arrayBuffer();
      });
      const logo1Buffer = await fetch(logo1).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch HABD.png");
        return res.arrayBuffer();
      });
      const logo2Buffer = await fetch(logo2).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch DMW_Patiala.png");
        return res.arrayBuffer();
      });

      // Add images to workbook
      const colorLogoId = workbook.addImage({
        buffer: colorLogoBuffer,
        extension: "png",
      });
      const logo1Id = workbook.addImage({
        buffer: logo1Buffer,
        extension: "png",
      });
      const logo2Id = workbook.addImage({
        buffer: logo2Buffer,
        extension: "png",
      });

      // Merge cells for logos and title
      worksheet.mergeCells("A1:B1"); 
      worksheet.mergeCells("D1:I1"); 
      worksheet.mergeCells("K1:L1"); 

      // Add ColorLogo centered in A1:B1
      worksheet.addImage(colorLogoId, {
        tl: { col: 0.5, row: 0.5 },
        ext: { width: 120, height: 60 },
      });
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      worksheet.addImage(logo1Id, {
        tl: { col: 3.2, row: 0.5 }, 
        ext: { width: 60, height: 60 },
      });

      const title =
        "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("D1").value = title;
      worksheet.getCell("D1").font = {
        bold: true,
        size: 14,
        name: "Helvetica",
      };
      worksheet.getCell("D1").alignment = {
        horizontal: "left", 
        vertical: "middle",
        indent: 8, 
      };

      worksheet.addImage(logo2Id, {
        tl: { col: 10.5, row: 0.5 },
        ext: { width: 80, height: 60 },
      });
      worksheet.getCell("K1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      worksheet.getRow(1).height = 65;

      worksheet.spliceRows(2, 1);
    } catch (error) {
      console.error("Error loading logo images:", error);
      setValidationError(
        "Failed to load logo images. Excel file will be generated without logos."
      );

      worksheet.mergeCells("A1:B1");
      worksheet.mergeCells("D1:I1");
      worksheet.mergeCells("K1:L1");

      worksheet.getCell("A1").value = "Logo 1";
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      worksheet.getCell("D1").value =
        "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("D1").font = {
        bold: true,
        size: 14,
        name: "Helvetica",
      };
      worksheet.getCell("D1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      worksheet.getCell("K1").value = "Logo 2";
      worksheet.getCell("K1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      worksheet.getRow(1).height = 65;
    }

    const userName = user?.username || "Guest";
    const downloadDate = new Date();
    const formattedDateTime = `${downloadDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })} ${downloadDate.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })} IST`;

    worksheet.getCell("A2").value = `Downloaded by: ${userName}`;
    worksheet.getCell("A2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    worksheet.getCell("L2").value = `Download Time: ${formattedDateTime}`;
    worksheet.getCell("L2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("L2").alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    worksheet.getRow(2).height = 20;

    // Add table headers (row 3)
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, name: "Helvetica" };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "808080" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    worksheet.getRow(3).height = 25;

    const locationLookup = locationPairs.reduce((acc, pair) => {
      const [key, value] = pair.split(" = ");
      acc[key] = value;
      return acc;
    }, {});

    const formatTrainDate = (trainId) => {
      if (!trainId) return "N/A";
      const parts = trainId.split("_");
      if (parts.length !== 7) return "N/A";
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    };

    const formatTrainTime = (trainId) => {
      if (!trainId) return "N/A";
      const parts = trainId.split("_");
      if (parts.length !== 7) return "N/A";
      const [hour, minute] = parts.slice(3, 5);
      return `${hour}:${minute}`;
    };

    const formatTrainName = (name) => {
      return name === "Train Name" ? "N/A" : name;
    };

    const formatTrainNumber = (number) => {
      return number === "Train Number" ? "N/A" : number;
    };

    const groupedTrains =
      responseData && responseData.trainDataBySiteDb
        ? Object.entries(responseData.trainDataBySiteDb).flatMap(
          ([siteName, trainList]) =>
            trainList.map((train, index) => ({
              ...train,
              SiteName: locationLookup[siteName] || siteName,
              serialNo: index + 1,
            }))
        )
        : [];

    // Add data rows
    groupedTrains.forEach((train) => {
      const rowData = [
        train.serialNo,
        train.SiteName,
        formatTrainDate(train.TrainId),
        formatTrainTime(train.TrainId),
        formatTrainName(train.TrainName),
        formatTrainNumber(train.TrainNumber),
        train.Description || "N/A",
        train.Direction || "N/A",
        train.TotalNoOfAxles || "N/A",
        train.AvgSpeed || "N/A",
        train.AlarmType === "hasAlarm" ? "Yes" : "No",
        train.WarningType === "hasWarning" ? "Yes" : "No",
      ];
      const row = worksheet.addRow(rowData);
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        // Highlight Alarm and Warning cells
        if (colNumber === 11 && cell.value === "Yes") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF0000" },
          };
          cell.font = { color: { argb: "FFFFFF" } };
        }
        if (colNumber === 12 && cell.value === "Yes") {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF00" },
          };
        }
      });
    });

    worksheet.columns = headers.map((_, index) => ({
      width: index === 1 ? 25 : 18,
    }));

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Report Details - ${formatDateForFile(dates.fromDate)} to ${formatDateForFile(dates.toDate)}.xlsx`;
      link.click();
    } catch (error) {
      console.error("Error generating Excel file:", error);
      setValidationError("Failed to generate Excel file. Please try again.");
    } finally {
      setDownloadLoading((prev) => ({ ...prev, excel: false }));
    }
  };

  const exportToPDF = async () => {
    setDownloadLoading((prev) => ({ ...prev, pdf: true }));
    const table = document.getElementById("train-data-table");

    // Check if table exists
    if (!table) {
      setValidationError("No data available to export to PDF");
      setDownloadLoading((prev) => ({ ...prev, pdf: false }));
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const headers = [];
    const rows = [];
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const logoWidth = 18;
    const logoHeight = 15;

    // Get user and download time
    const user = JSON.parse(localStorage.getItem("user")) || { username: "Guest" };
    const userName = user.username;
    const downloadDate = new Date();
    const formattedDateTime = `${downloadDate.toLocaleDateString()} ${downloadDate.toLocaleTimeString()}`;

    try {
      const headerCells = table.querySelectorAll("thead th");
      for (let i = 0; i < headerCells.length - 1; i++) {
        headers.push(headerCells[i].innerText.trim());
      }

      const rowCells = table.querySelectorAll("tbody tr");
      rowCells.forEach((row) => {
        const rowData = [];
        const cells = row.querySelectorAll("td");
        for (let i = 0; i < cells.length - 1; i++) { 
          rowData.push(cells[i].innerText.trim());
        }
        rows.push(rowData);
      });

      const calculateColumnWidths = () => {
        const colWidths = Array(headers.length).fill(0);
        const padding = 4; 
        headers.forEach((header, i) => {
          const width = doc.getTextWidth(header) + padding * 2;
          colWidths[i] = Math.max(colWidths[i], width);
        });

        // Measure data widths
        rows.forEach(row => {
          row.forEach((cell, i) => {
            // Split text into lines and measure the widest line
            const lines = doc.splitTextToSize(cell, pageWidth - 20);
            const width = Math.max(...lines.map(line => doc.getTextWidth(line))) + padding * 2;
            colWidths[i] = Math.max(colWidths[i], width);
          });
        });

        // Normalize widths to fit page
        const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
        const scaleFactor = (pageWidth - 20) / totalWidth;

        return colWidths.map(width => width * scaleFactor);
      };

      const colWidths = calculateColumnWidths();
      const xOffset = 10; // Left margin

      // Header function
      const addHeader = () => {
        doc.setTextColor(0, 0, 0);

        // Add ColorLogo
        if (colorLogo) {
          const logoX = 10;
          const logoY = 5;
          const logoW = 35;
          const logoH = logoHeight;
          doc.addImage(colorLogo, "PNG", logoX, logoY, logoW, logoH);
          doc.link(logoX, logoY, logoW, logoH, { url: "https://noviusrailtech.com/" });
        }

        // Add logo2
        if (logo2) {
          doc.addImage(logo2, "PNG", pageWidth - logoWidth - 10, 5, logoWidth, logoHeight);
        }

        // Add title
        const titleText = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        const textWidth = doc.getTextWidth(titleText);
        const centerX = pageWidth / 2;
        const offsetX = 20;
        const textX = centerX - textWidth / 2 + offsetX;

        // Add logo1
        if (logo1) {
          const logo1X = textX - logoWidth - 4;
          doc.addImage(logo1, "PNG", logo1X, 5, logoWidth, logoHeight);
        }

        doc.text(titleText, textX, 15);
        doc.setDrawColor(200, 200, 200);
        doc.line(10, 22, pageWidth - 10, 22);
      };

      // Footer function
      const addFooter = () => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const pageStr = `Page ${i} of ${pageCount}`;
          doc.text(pageStr, pageWidth - 14 - doc.getTextWidth(pageStr), pageHeight - 10);

          doc.setTextColor(80);
          doc.setFont("helvetica", "italic");
          const footerText = `Downloaded by: ${userName} | Download Time: ${formattedDateTime}`;
          doc.text(footerText, 14, pageHeight - 10);
        }
      };

      // Function to draw table header
      const drawTableHeader = (startY) => {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);

        const headerHeight = 8;
        let x = xOffset;

        doc.setFillColor(220, 220, 220);
        doc.rect(xOffset, startY, colWidths.reduce((a, b) => a + b, 0), headerHeight, "F");

        headers.forEach((header, index) => {
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.1);
          doc.rect(x, startY, colWidths[index], headerHeight);

          const lines = doc.splitTextToSize(header, colWidths[index] - 4);
          const textY = startY + headerHeight / 2 + 1; 
          doc.text(lines, x + 2, textY, {
            align: 'left',
            maxWidth: colWidths[index] - 4
          });

          x += colWidths[index];
        });

        return startY + headerHeight;
      };

      let y = 30; 
      const cellPadding = 2;

      addHeader();

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      const reportTitle = `Report Details - ${formatDateForFile(dates.fromDate)} to ${formatDateForFile(dates.toDate)}`;
      doc.text(reportTitle, xOffset, y);
      y += 10;

      // Draw table headers
      y = drawTableHeader(y);

      // Set font for data rows
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      // Render table rows
      rows.forEach((row, rowIndex) => {
        // Calculate required height for this row
        let maxLines = 1;
        row.forEach((cell, i) => {
          const lines = doc.splitTextToSize(cell, colWidths[i] - cellPadding * 2);
          maxLines = Math.max(maxLines, lines.length);
        });
        const rowHeight = Math.max(6, maxLines * 3 + cellPadding * 2);

        // Check if we need a new page
        if (y + rowHeight > pageHeight - 25) {
          doc.addPage();
          addHeader();
          y = 30;

          // Add Report Title on new page
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(reportTitle, xOffset, y);
          y += 10;

          // Draw table headers on new page
          y = drawTableHeader(y);

          // Reset font for data
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
        }

        // Draw row background (alternating colors)
        if (rowIndex % 2 === 0) {
          doc.setFillColor(255, 255, 255);
        } else {
          doc.setFillColor(248, 248, 248);
        }
        doc.rect(xOffset, y, colWidths.reduce((a, b) => a + b, 0), rowHeight, "F");

        // Draw cell borders and text
        let x = xOffset;
        row.forEach((cell, i) => {
          // Draw cell border
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.1);
          doc.rect(x, y, colWidths[i], rowHeight);

          // Draw cell text
          const lines = doc.splitTextToSize(cell, colWidths[i] - cellPadding * 2);
          let textY = y + cellPadding + 3; // Start position for text

          lines.forEach((line, lineIndex) => {
            doc.text(line, x + cellPadding, textY + (lineIndex * 3), {
              align: 'left',
              maxWidth: colWidths[i] - cellPadding * 2
            });
          });

          x += colWidths[i];
        });

        y += rowHeight;
      });

      addFooter();

      await new Promise((resolve) => setTimeout(resolve, 500));

      doc.save(`Report Details - ${formatDateForFile(dates.fromDate)} to ${formatDateForFile(dates.toDate)}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setValidationError("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadLoading((prev) => ({ ...prev, pdf: false }));
    }
  };

  return (
    <div className="w-full mx-auto p-4 sm:p-4 bg-gray-100 mt-24 sm:mt-10 md:mt-0">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}
      <ReportsFilters
        dates={dates}
        handleDateChange={handleDateChange}
        handleSearch={handleSearch}
        coachType={coachType}
        direction={direction}
        alarmType={alarmType}
        location={location}
        sites={sites}
        handleAllChange={handleAllChange}
        handleIndividualChange={handleIndividualChange}
        setCoachTypes={setCoachTypes}
        setDirection={setDirection}
        setAlarmType={setAlarmType}
        setLocation={setLocation}
        trainNumber={trainNumber}
        rfid={rfid}
        coachNumber={coachNumber}
        handleTrainNumberChange={handleTrainNumberChange}
        handleRfidChange={handleRfidChange}
        handleCoachNumberChange={handleCoachNumberChange}
      />

      <div className="mt-2 bg-gray-50 rounded-md">
        <div className="bg-white border border-gray-200 rounded-md shadow-sm transition-shadow duration-200 p-3">
          <h1 className="text-sm font-semibold text-gray-800 flex items-center gap-1 mb-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Summary Report
          </h1>
          {responseData && <SummaryReport responseData={responseData} />}
        </div>
      </div>

      <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="bg-blue-50 rounded-md p-2">
              <TrainIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Train Details
              </h3>
              <p className="text-[11px] text-gray-500">
                {totalTrains} trains found across {groupedTrains.length} locations
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={exportToExcel}
              className="flex items-center justify-center p-1.5 bg-white border border-gray-200 rounded-md hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
              aria-label="Export to Excel"
              title="Export to Excel"
              disabled={downloadLoading.excel}
            >
              {downloadLoading.excel ? (
                <Vortex
                  visible={true}
                  height={16}
                  width={16}
                  ariaLabel="vortex-loading"
                  wrapperStyle={{}}
                  wrapperClass="vortex-wrapper"
                  colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
                />
              ) : (
                <>
                  <PiMicrosoftExcelLogoFill size={16} className="text-green-500" />
                  <span className="ml-1 text-[11px]">Excel</span>
                </>
              )}
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center justify-center p-1.5 bg-white border border-gray-200 rounded-md hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
              aria-label="Export to PDF"
              title="Export to PDF"
              disabled={downloadLoading.pdf}
            >
              {downloadLoading.pdf ? (
                <Vortex
                  visible={true}
                  height={16}
                  width={16}
                  ariaLabel="vortex-loading"
                  wrapperStyle={{}}
                  wrapperClass="vortex-wrapper"
                  colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
                />
              ) : (
                <>
                  <BsFileEarmarkPdfFill size={16} className="text-red-500" />
                  <span className="ml-1 text-[11px]">PDF</span>
                </>
              )}
            </button>
            <div className="relative" ref={sortIconRef}>
              <ArrowUpDown
                className="h-4 w-4 cursor-pointer text-gray-600 hover:text-blue-600 transition-colors duration-200"
                onClick={() => setIsSortOpen(!isSortOpen)}
                title="Sort Trains"
              />
              {isSortOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-sm z-[5] overflow-hidden"
                  ref={sortDropdownRef}
                >
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-[11px] font-semibold text-gray-700">
                      Sort By
                    </span>
                  </div>
                  <ul className="py-1 text-[11px] text-gray-800">
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "trainType" &&
                        sortOption.order === "asc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("trainType", "asc")}
                    >
                      Train Type (A-Z)
                    </li>
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "trainType" &&
                        sortOption.order === "desc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("trainType", "desc")}
                    >
                      Train Type (Z-A)
                    </li>
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "trainDate" &&
                        sortOption.order === "asc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("trainDate", "asc")}
                    >
                      Train Date (Oldest First)
                    </li>
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "trainDate" &&
                        sortOption.order === "desc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("trainDate", "desc")}
                    >
                      Train Date (Newest First)
                    </li>
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "siteName" &&
                        sortOption.order === "asc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("siteName", "asc")}
                    >
                      Site Name (A-Z)
                    </li>
                    <li
                      className={`px-3 py-1.5 cursor-pointer font-medium transition-colors duration-200 ${sortOption.field === "siteName" &&
                        sortOption.order === "desc"
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-500 hover:text-white"
                        }`}
                      onClick={() => handleSortSelect("siteName", "desc")}
                    >
                      Site Name (Z-A)
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Train Table Section */}
        <TrainDetailsTable
          responseData={responseData}
          groupedTrains={groupedTrains}
          sites={sites}
          locationPairs={locationPairs}
          downloadLoading={downloadLoading}
          setDownloadLoading={setDownloadLoading}
          setValidationError={setValidationError}
        />
      </div>

      {validationError && (
        <ValidationAlert
          error={validationError}
          onClose={() => setValidationError(null)}
        />
      )}
      {showNoDataPopup && (
        <NoDataAlert onClose={() => setShowNoDataPopup(false)} />
      )}
    </div>
  );
};


export default Reports;